'use client';

import React from 'react';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export default function LoadingSpinner({ size = 'md', className = '' }: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className="relative">
        {/* Outer ring */}
        <div
          className={`${sizeClasses[size]} rounded-full border-4 border-gray-200/20`}
          aria-hidden="true"
        />
        {/* Spinning ring */}
        <div
          className={`${sizeClasses[size]} absolute top-0 left-0 animate-spin rounded-full border-4 border-transparent border-t-white`}
          aria-hidden="true"
        />
        {/* Inner glow effect */}
        <div
          className={`${sizeClasses[size]} absolute top-0 left-0 animate-spin rounded-full border-4 border-transparent border-t-green-400/60`}
          style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}

// Alternative elegant spinner with multiple rings
export function ElegantSpinner({ size = 'md', className = '' }: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-20 h-20',
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className="relative">
        {/* Ring 1 */}
        <div
          className={`${sizeClasses[size]} animate-spin rounded-full border-2 border-white/10`}
          style={{ animationDuration: '1s' }}
          aria-hidden="true"
        />
        {/* Ring 2 */}
        <div
          className={`${sizeClasses[size]} absolute top-0 left-0 animate-spin rounded-full border-2 border-transparent border-t-green-400`}
          style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}
          aria-hidden="true"
        />
        {/* Ring 3 */}
        <div
          className={`${sizeClasses[size]} absolute top-0 left-0 animate-spin rounded-full border-2 border-transparent border-r-blue-400`}
          style={{ animationDuration: '2s' }}
          aria-hidden="true"
        />
        {/* Center dot */}
        <div
          className={`${sizeClasses[size]} absolute top-0 left-0 flex items-center justify-center`}>
          <div className="h-1 w-1 animate-pulse rounded-full bg-white" />
        </div>
      </div>
    </div>
  );
}

// Minimalist spinner
export function MinimalSpinner({ size = 'md', className = '' }: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-10 h-10',
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div
        className={`${sizeClasses[size]} animate-spin rounded-full border-2 border-white/30 border-t-white`}
        aria-hidden="true"
      />
    </div>
  );
}
